<script setup>
import {Dialog, DialogOverlay, DialogPanel, TransitionChild, TransitionRoot} from "@headlessui/vue";
import {Icon} from "@iconify/vue";
import {computed, onMounted, reactive, ref} from "vue";
import {useMainOrdersStore} from "@/stores/mainOrders.js";
import {useConfigStore} from "@/stores/config.js";
import showAlert from "@/utils/showAlert.js";
import {showMessage} from "@/utils/showMessage.js";
import {useUserStore} from "@/stores/user.js";

const emit = defineEmits(['closeModal', 'deletedAccount']);
const props = defineProps({
  id: Number
})
const isLoading = ref(false);
const deleting = ref(false);
const saving = ref(false);
const ordersStore = useMainOrdersStore();
const configStore = useConfigStore()
const userStore = useUserStore();
const games = computed(() => configStore.getGamesByService('Currency') || [])
const form = reactive({
  "id": "",
  "game": "",
  "title": "",
  "username": "",
  "password": "",
  "isActive": "",
})

function closeModal() {
  emit('closeModal');
}

async function getGameAccount() {
  isLoading.value = true
  await ordersStore.action_getGameAccount({id: props.id})
  const data = ordersStore.gameAccount;
  Object.assign(form, data)
  isLoading.value = false
}

onMounted(async () => {
  await getGameAccount();
})

async function onSave() {
  // User verification
  const ok = await showAlert('You are updating game account', 'Are you sure?', 'Yes', 'No');
  if (!ok) return

  // API call
  try {
    saving.value = true
    const res = await ordersStore.action_updateGameAccount({
      "id": form.id,
      "game": form.game,
      "title": form.title,
      "username": form.username,
      "password": form.password,
      "isActive": form.isActive
    })

    if (res === true) {
      showMessage('Game Account Updated', 'success');
      emit('updatedAccount')
    } else {
      showMessage(res, 'error');
    }
  } catch (err) {
    showMessage(err, 'error');
  } finally {
    saving.value = false
  }
}

async function onDelete() {
  // User verification
  const ok = await showAlert('You are deleting game account', 'Are you sure?', 'Yes', 'No');
  if (!ok) return

  // API call
  try {
    deleting.value = true
    const res = await ordersStore.action_removeGameAccount({id: form.id})

    if (res === true) {
      showMessage('Game Account Deleted', 'success');
      emit('deletedAccount')
    } else {
      showMessage(res, 'error');
    }
  } catch (err) {
    showMessage(err, 'error');
  } finally {
    deleting.value = false
  }
}
</script>

<template>
  <TransitionRoot appear show as="template">
    <Dialog as="div" @close="closeModal" class="relative z-[999]">
      <TransitionChild
          as="template"
          enter="duration-300 ease-out"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="duration-200 ease-in"
          leave-from="opacity-100"
          leave-to="opacity-0"
      >
        <DialogOverlay class="fixed inset-0 bg-[black]/60"/>
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">
        <div class="flex h-full overflow-hidden items-center justify-center px-4 py-8">
          <TransitionChild
              as="template"
              enter="duration-300 ease-out"
              enter-from="opacity-0 scale-95"
              enter-to="opacity-100 scale-100"
              leave="duration-200 ease-in"
              leave-from="opacity-100 scale-100"
              leave-to="opacity-0 scale-95"
          >
            <DialogPanel
                class="w-full flex flex-col overflow-hidden rounded-md max-w-max h-auto text-left align-middle shadow-xl transition-all bg-white dark:bg-[#0e1726] text-black-600 dark:text-white">
              <div class="h-full flex flex-col">
                <div class="flex flex-row gap-10 justify-between px-4 border-b border-slate-700">
                  <div class="text-base py-3">
                    Edit {{ form.title }}
                  </div>
                  <div class="flex flex-row items-center gap-4">
                    <div class="h-full w-[1px] bg-slate-700"></div>
                    <button
                        type="button"
                        class="text-white-dark outline-none hover:text-red-500"
                        @click="closeModal"
                    >
                      <Icon icon="pajamas:close-xs"/>
                    </button>
                  </div>
                </div>
                <div class="p-4 flex-1 overflow-auto h-full flex flex-col gap-6">
                  <p v-if="isLoading">Loading...</p>
                  <div v-else class="space-y-5">
                    <div class="flex flex-col gap-0.5 text-sm w-max">
                      <label for="game">Game:</label>
                      <div
                          class="flex flex-row gap-2 items-center w-full bg-slate-900 border border-slate-700 text-slate-300 px-1 rounded-md h-[40px]">
                        <select id="game"
                                v-model="form.game"
                                :disabled="games.length === 0"
                                class="bg-slate-900 h-full focus:ring-0 focus-visible:ring-0 focus-visible:outline-none w-full">
                          <option value="" disabled class="disabled:bg-slate-900 disabled:hover:bg-slate-900">--
                            Select
                            Game
                            --
                            &nbsp
                          </option>
                          <option
                              v-for="game in games"
                              :key="game.game"
                              :value="game.game"
                          >
                            {{ game.game }}&nbsp
                          </option>
                        </select>
                      </div>
                    </div>
                    <div class="flex-1 flex flex-col gap-0.5 text-sm w-full">
                      <label for="title" class="text-slate-900 dark:text-slate-300">Title:</label>
                      <div class="w-full dark:bg-slate-900 border border-slate-700 pr-3 rounded-md h-[40px]">
                        <input id="title"
                               type="text"
                               v-model="form.title"
                               class="bg-transparent w-full pl-3 block focus:outline-none text-slate-900 dark:text-slate-200 h-full rounded-md autofill-fix dark:[--shadow-color:#0f172a] [--text-color:#0f172a] dark:[--text-color:rgb(203,213,225)] dark:[--caret-color:rgb(203,213,225)]">
                      </div>
                    </div>
                    <div class="flex-1 flex flex-col gap-0.5 text-sm w-full">
                      <label for="title" class="text-slate-900 dark:text-slate-300">Username:</label>
                      <div class="w-full dark:bg-slate-900 border border-slate-700 pr-3 rounded-md h-[40px]">
                        <input id="title"
                               type="text"
                               v-model="form.username"
                               class="bg-transparent w-full pl-3 block focus:outline-none text-slate-900 dark:text-slate-200 h-full rounded-md autofill-fix dark:[--shadow-color:#0f172a] [--text-color:#0f172a] dark:[--text-color:rgb(203,213,225)] dark:[--caret-color:rgb(203,213,225)]">
                      </div>
                    </div>
                    <div class="flex-1 flex flex-col gap-0.5 text-sm w-full">
                      <label for="title" class="text-slate-900 dark:text-slate-300">Password:</label>
                      <div class="w-full dark:bg-slate-900 border border-slate-700 pr-3 rounded-md h-[40px]">
                        <input id="title"
                               type="text"
                               v-model="form.password"
                               class="bg-transparent w-full pl-3 block focus:outline-none text-slate-900 dark:text-slate-200 h-full rounded-md autofill-fix dark:[--shadow-color:#0f172a] [--text-color:#0f172a] dark:[--text-color:rgb(203,213,225)] dark:[--caret-color:rgb(203,213,225)]">
                      </div>
                    </div>
                    <div class="flex-1 flex flex-row gap-2 text-sm w-full">
                      <div class="dark:bg-slate-900 rounded-md">
                        <input id="title"
                               type="checkbox"
                               v-model="form.isActive"
                               class="bg-transparent w-full pl-3 block focus:outline-none text-slate-900 dark:text-slate-200 h-full rounded-md autofill-fix dark:[--shadow-color:#0f172a] [--text-color:#0f172a] dark:[--text-color:rgb(203,213,225)] dark:[--caret-color:rgb(203,213,225)]">
                      </div>
                      <label for="title" class="text-slate-900 dark:text-slate-300">Is Active</label>

                    </div>
                  </div>
                </div>
                <div class="p-3 flex justify-end gap-3">
                  <button v-if="userStore.hasPermission('BIG_BOSS') || userStore.hasPermission('GAME_ACCOUNTS_REMOVE')"
                          @click="onDelete" class="btn btn-outline-primary py-1">
                    <span v-if="deleting"
                          class="animate-[spin_2s_linear_infinite] inline-block align-middle ltr:mr-2 rtl:ml-2 shrink-0"></span>
                    Remove
                  </button>
                  <button @click="onSave" class="btn btn-outline-primary py-1">
                    <span v-if="saving"
                          class="animate-[spin_2s_linear_infinite] inline-block align-middle ltr:mr-2 rtl:ml-2 shrink-0"></span>
                    Save
                  </button>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<style scoped lang="scss">

</style>